<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold">
          Datamap
          <span class="primary--text">Checker</span>
        </h1>
        <p class="text-body-1 grey--text text--darken-1">
          Validate survey labels against Decipher datamaps. Find missing variables, catch naming discrepancies, and verify scale lengths/directions.
        </p>
      </v-col>
    </v-row>

    <v-row>
      <!-- INPUTS COLUMN -->
      <v-col cols="12" md="3">
        <v-card class="elevation-2 fill-height d-flex flex-column">
          <v-card-title class="grey lighten-4 text-subtitle-1 font-weight-bold">
            Configuration
          </v-card-title>
          <v-divider></v-divider>
          
          <v-card-text class="flex-grow-1">
            <v-text-field
              v-model="decipherPath"
              label="Decipher Project Path"
              placeholder="e.g. selfserve/9d3/12345"
              outlined
              dense
              class="mb-2"
              hide-details="auto"
              :prepend-inner-icon="icons.mdiFolderSearchOutline"
            ></v-text-field>

            <!-- <div class="text-subtitle-2 mt-4 mb-2 black--text">
              View Unmapped Labels ({{ UnmappedLabels.length }})
            </div>
            <v-textarea
              v-model="dropdownOptions"
              outlined
              dense
              rows="20"
              :error-messages="inputError"
              placeholder='[{"label": "Q1", "scale": 5}]'
              class="font-monospace"
            ></v-textarea> -->
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn 
              color="primary" 
              large 
              block 
              :disabled="!isValidForm" 
              @click="runCheck"
            >
              <v-icon left>{{icons.mdiPlayCircle}}</v-icon>
              Run Validation
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- RESULTS COLUMN -->
      <v-col cols="12" md="9">
        <v-card class="elevation-2 fill-height position-relative d-flex flex-column">
          <v-card-title class="grey lighten-4" flex-wrap>
            <span class="text-subtitle-1 font-weight-bold mr-4">
              {{ currentView === 'table' ? 'Validation Results' : 'Validation Recap' }}
            </span>
            
            <!-- Filters -->
            <v-chip-group v-model="statusFilter" active-class="primary--text" mandatory v-if="results.length > 0 && currentView === 'table'">
              <v-chip value="exact" small color="success" outlined>Exact 1:1 ({{ exactCount }})</v-chip>
              <v-chip value="mapped" small color="primary" outlined>Mapped ({{ mappedCount }})</v-chip>
              <v-chip value="suggested" small color="warning" outlined>Suggested ({{ suggestedCount }})</v-chip>
              <v-chip value="unmapped" small color="error" outlined>Unmapped ({{ unmappedCount }})</v-chip>
              <v-chip value="all" small>All ({{ results.length }})</v-chip>
            </v-chip-group>

            <v-spacer></v-spacer>

            <!-- Navigation / Export Actions -->
            <div v-if="results.length > 0">
              <!-- Table View Action -->
              <v-tooltip bottom :disabled="!hasBlockingErrors">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn 
                      v-if="currentView === 'table'" 
                      color="primary" 
                      @click="currentView = 'recap'" 
                      :disabled="hasBlockingErrors"
                    >
                      Next Step
                      <v-icon right>{{ icons.mdiArrowRight }}</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Cannot proceed until red Scale Mismatch errors are resolved or deleted.</span>
              </v-tooltip>

              <!-- Recap View Actions -->
              <div v-if="currentView === 'recap'" class="d-flex align-center">
                <v-btn text color="secondary" @click="currentView = 'table'" class="mr-3">
                  <v-icon left>{{ icons.mdiArrowLeft }}</v-icon> Back to Table
                </v-btn>
                
                <!-- NEW API SAVE BUTTONS -->
                <v-btn color="primary" outlined class="mr-3" @click="saveToDatabase(false)" :loading="isSaving">
                  <v-icon left>{{ icons.mdiCheckCircle }}</v-icon> Save Draft
                </v-btn>
                <v-btn color="success" class="mr-3" @click="saveToDatabase(true)" :loading="isSaving">
                  <v-icon left>{{ icons.mdiCheckDecagram }}</v-icon> Finalize & Lock
                </v-btn>

                <!-- Existing local export -->
                <v-btn color="grey darken-3" dark @click="exportJSON" :disabled="isSaving">
                  <v-icon left>{{ icons.mdiCodeJson }}</v-icon> Download JSON
                </v-btn>
              </div>
            </div>
          </v-card-title>
          
          <v-divider></v-divider>

          <!-- Loading Overlay -->
          <v-overlay :value="isChecking" absolute color="white" opacity="0.9" z-index="5">
            <div class="d-flex flex-column align-center justify-center text-center">
              <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-6"></v-progress-circular>
              <transition name="fade" mode="out-in">
                <h3 :key="currentLoadingText" class="text-h6 primary--text font-weight-medium">
                  {{ currentLoadingText }}
                </h3>
              </transition>
              <p class="text-body-2 grey--text mt-2 max-w-sm">
                Parsing datamap structure and automatically mapping expected labels...
              </p>
            </div>
          </v-overlay>

          <!-- Empty State -->
          <v-card-text v-if="results.length === 0 && !isChecking" class="d-flex flex-column align-center justify-center fill-height py-12">
            <v-icon size="64" color="grey lighten-2">{{icons.mdiLinkVariant}}</v-icon>
            <p class="grey--text text--darken-1 mt-4">Run the mapping to link Datamap fields to your Expected labels.</p>
          </v-card-text>

          <!-- ================= TABLE VIEW ================= -->
          <template v-else-if="currentView === 'table'">
            <!-- Expected Labels Summary -->
            <v-card-text class="pt-4 pb-0">
              <v-expansion-panels flat outlined class="mb-2">
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-medium py-2 min-h-0 text-caption">
                    View Unmapped Labels ({{ UnmappedLabels.length }})
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex flex-wrap mt-2">
                      <v-chip
                        v-for="(label, idx) in UnmappedLabels"
                        :key="idx"
                        small
                        color="primary"
                        outlined
                        class="ma-1 font-monospace text-caption"
                      >
                        {{ label }}
                      </v-chip>
                      <span v-if="UnmappedLabels.length === 0" class="grey--text text-caption">
                        No labels mapped yet.
                      </span>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel v-if="flaggedItems.length > 0" :value="0">
                  <v-expansion-panel-header class="font-weight-bold warning--text py-2 min-h-0 text-subtitle-2">
                    <div class="d-flex align-center">
                      <v-icon left color="warning" small class="mr-2">{{ icons.mdiFlag }}</v-icon>
                      Flagged Variables & Near Matches ({{ flaggedItems.length }})
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="bg-grey-lighten-5 pt-3">
                    <p class="text-caption grey--text text--darken-1 mb-2">
                      These datamap labels share a prefix or group with your expected variables but are not exact matches. Review the options and map them below.
                    </p>
                    <v-list dense class="transparent">
                      <v-list-item v-for="(item, idx) in flaggedItems" :key="idx" class="px-0 py-2 border-bottom-dashed align-start">
                        
                        <!-- Datamap Label & Sub ID -->
                        <v-list-item-content class="py-0" style="max-width: 20%;">
                          <v-list-item-title class="font-monospace font-weight-bold">{{ item.datamap_label }}</v-list-item-title>
                          <!-- <v-list-item-subtitle v-if="item.subId" class="error--text text-caption font-weight-medium mt-1">
                            Suffix ID: [{{ item.subId }}]
                          </v-list-item-subtitle> -->
                        </v-list-item-content>
                        <v-list-item-content class="py-0" style="max-width: 10%;">
                             <v-chip x-small :color="getStatusColor(item.status)" :text-color="['warning', 'error'].includes(getStatusColor(item.status)) ? 'black' : 'white'" label>
                                {{ item.status.toUpperCase() }}
                              </v-chip>
                        </v-list-item-content>
                        
                        <!-- Comprehensive Context Viewer -->
                        <v-list-item-content class="py-0 px-3" style="max-width: 40%;">
                          
                          <!-- Variable's direct title -->
                          <div v-if="item.title" class="mb-2 text-caption lh-tight">
                            <span class="font-weight-bold grey--text text--darken-2"></span>
                            <span class="ml-1 v-html-safe" >{{ item.title }}</span>
                          </div>

                          <!-- Grouped Siblings (vgroup based) 
                          <div v-if="item.siblings && item.siblings.length > 0" class="mb-2">
                            <span class="text-caption font-weight-bold grey--text text--darken-2">Related Options in Group ({{ item.vgroup }}):</span>
                            <div class="d-flex flex-wrap mt-1">
                              <v-chip 
                                v-for="(sib, i) in item.siblings" :key="'sib'+i" 
                                small 
                                :color="sib.label === item.datamap_label ? 'warning' : 'grey darken-3'"
                                :class="sib.label === item.datamap_label ? 'black--text font-weight-bold' : 'grey--text text--darken-2'"
                                class="mr-1 mb-1 text-wrap h-auto py-1"
                              >
                                <strong class="font-monospace">{{ sib.subId || sib.label }}:</strong>&nbsp;<span v-html="sib.title"></span>
                              </v-chip>
                            </div>
                          </div>

                          <div v-if="item.parentRows && item.parentRows.length > 0" class="mb-2">
                            <span class="text-caption font-weight-bold grey--text text--darken-2">Grid Rows:</span>
                            <div class="d-flex flex-wrap mt-1">
                              <v-chip 
                                v-for="(r, i) in item.parentRows" :key="'r'+i" 
                                small 
                                :color="r.label === item.subId ? 'warning' : 'grey darken-3'"
                                :class="r.label === item.subId ? 'black--text font-weight-bold' : 'grey--text text--darken-2'"
                                class="mr-1 mb-1 text-wrap h-auto py-1"
                              >
                                <strong>{{ r.label }}:</strong>&nbsp;<span v-html="r.title || r.text"></span>
                              </v-chip>
                            </div>
                          </div>

                          <div v-if="item.parentCols && item.parentCols.length > 0" class="mb-2">
                            <span class="text-caption font-weight-bold grey--text text--darken-2">Grid Cols / Choices:</span>
                            <div class="d-flex flex-wrap mt-1">
                              <v-chip 
                                v-for="(c, i) in item.parentCols" :key="'c'+i" 
                                small 
                                :color="c.label === item.subId || c.value === item.subId ? 'warning' : 'grey darken-3'"
                                :class="c.label === item.subId || c.value === item.subId ? 'black--text font-weight-bold' : 'grey--text text--darken-2'"
                                class="mr-1 mb-1 text-wrap h-auto py-1"
                              >
                                <strong>{{ c.label || c.value }}:</strong>&nbsp;<span v-html="c.title || c.text"></span>
                              </v-chip>
                            </div>
                          </div>

                          <span v-if="!item.title && (!item.siblings || !item.siblings.length) && (!item.parentRows || !item.parentRows.length) && (!item.parentCols || !item.parentCols.length)" class="text-caption font-italic grey--text">
                            No context data available in datamap.
                          </span>-->
                        </v-list-item-content>

                        <!-- Mapping Dropdown -->
                        <v-list-item-action class="ma-0 py-0" style="width: 30%;">
                          <v-autocomplete
                            v-model="item.mapped_to"
                            :items="dropdownOptions"
                            placeholder="Select expected label..."
                            dense
                            outlined
                            hide-details
                            clearable
                            class="font-monospace w-100"
                            @change="onMappingChange(item)"
                          >
                            <template #item="{ item: dropdownItem, attrs, on }">
                              <v-list-item v-bind="attrs" v-on="on" :disabled="dropdownItem.disabled">
                                <v-list-item-content>
                                  <v-list-item-title :class="{'grey--text': dropdownItem.disabled}">
                                    {{ dropdownItem.text }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </template>
                          </v-autocomplete>
                        </v-list-item-action>

                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>

            <!-- Data Table -->
            <v-data-table
              :headers="headers"
              :items="filteredResults"
              :items-per-page="-1"
              hide-default-footer
              dense
              class="elevation-0 flex-grow-1"
            >
              <template #item.datamap_label="{ item }">
                <span class="font-monospace font-weight-medium">{{ item.datamap_label }}</span>
              </template>

              <template #item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" :text-color="getStatusColor(item.status) === 'warning' ? 'black' : 'white'" small label>
                  <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                  {{ item.status.toUpperCase() }}
                </v-chip>
              </template>

              <template #item.mapped_to="{ item }">
                <v-autocomplete
                  v-model="item.mapped_to"
                  :items="dropdownOptions"
                  placeholder="Select an expected label..."
                  dense
                  outlined
                  hide-details
                  clearable
                  class="my-1 font-monospace"
                  style="max-width: 350px;"
                  @change="onMappingChange(item)"
                >
                  <template #item="{ item: dropdownItem, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on" :disabled="dropdownItem.disabled">
                      <v-list-item-content>
                        <v-list-item-title :class="{'grey--text': dropdownItem.disabled}">
                          {{ dropdownItem.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="dropdownItem.disabled">
                        <v-chip x-small color="grey lighten-2">Assigned</v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </template>

              <!-- Scale Warnings Column with Deletable Chips & Tooltips -->
              <template #item.warnings="{ item }">
                <div v-if="item.warnings && item.warnings.length > 0">
                  <v-tooltip bottom v-for="(warn, idx) in item.warnings" :key="warn.id">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip 
                        v-bind="attrs"
                        v-on="on"
                        :color="warn.type === 'error' ? 'error' : 'warning'" 
                        :text-color="warn.type === 'error' ? 'white' : 'black'"
                        small 
                        close
                        class="mb-1 d-flex"
                        @click:close="removeWarning(item, idx)"
                      >
                        <v-icon left small>{{ warn.type === 'error' ? icons.mdiAlertCircle : icons.mdiAlert }}</v-icon>
                        {{ warn.text }}
                      </v-chip>
                    </template>
                    <!-- Tooltip Content -->
                    <div class="pa-1">
                      <div class="font-weight-bold mb-1 border-bottom">Datamap Choices (Values):</div>
                      <template v-if="warn.raw_values && warn.raw_values.length > 0">
                        <div v-for="(val, vIdx) in warn.raw_values" :key="vIdx" class="text-caption">
                          <span class="font-weight-bold">[{{ val.value }}]</span> {{ val.title || val.label }}
                        </div>
                      </template>
                      <div v-else class="text-caption font-italic mt-1">No choice values found.</div>
                    </div>
                  </v-tooltip>
                </div>
                <span v-else class="grey--text text-caption">-</span>
              </template>
            </v-data-table>
          </template>

          <!-- ================= RECAP VIEW ================= -->
          <div v-else-if="currentView === 'recap'" class="flex-grow-1 overflow-y-auto pa-6">
            <v-row class="mb-6">
              <v-col cols="12" md="3">
                <v-card outlined class="text-center fill-height bg-grey-lighten-5">
                  <v-card-subtitle class="font-weight-bold text-uppercase pb-1">Exact (1:1)</v-card-subtitle>
                  <v-card-text class="text-h3 success--text font-weight-black">{{ exactCount }}</v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="text-center fill-height bg-grey-lighten-5">
                  <v-card-subtitle class="font-weight-bold text-uppercase pb-1">Mapped</v-card-subtitle>
                  <v-card-text class="text-h3 success--text font-weight-black">{{ mappedCount }}</v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="text-center fill-height bg-grey-lighten-5">
                  <v-card-subtitle class="font-weight-bold text-uppercase pb-1">Suggested / Need Review</v-card-subtitle>
                  <v-card-text class="text-h3 warning--text font-weight-black">{{ suggestedCount }}</v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="text-center fill-height bg-grey-lighten-5">
                  <v-card-subtitle class="font-weight-bold text-uppercase pb-1">Unmapped</v-card-subtitle>
                  <v-card-text class="text-h3 error--text font-weight-black">{{ unmappedCount }}</v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="mb-4"></v-divider>

            <h3 class="text-h6 mb-3 d-flex align-center">
              <v-icon left color="warning">{{ icons.mdiAlert }}</v-icon>
              Remaining Warnings
            </h3>
            
            <v-alert v-if="itemsWithWarnings.length === 0" type="success" text dense outlined>
              Great job! All scale and direction warnings have been cleared or resolved.
            </v-alert>

            <v-list v-else dense class="transparent">
              <v-list-item v-for="(item, i) in itemsWithWarnings" :key="i" class="px-0 mb-3 border-bottom-dashed">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold mb-1">
                    <span class="font-monospace">{{ item.datamap_label }}</span>
                    <v-icon mx-2 small>{{ icons.mdiArrowRight }}</v-icon>
                    <span class="primary--text">{{ item.mapped_to }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip 
                      v-for="warn in item.warnings" 
                      :key="warn.id"
                      :color="warn.type === 'error' ? 'error' : 'warning'"
                      :text-color="warn.type === 'error' ? 'white' : 'black'"
                      small
                      class="mr-2 mb-1"
                    >
                      {{ warn.text }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import store from '@/store'
import { ref, computed, onMounted, onUnmounted } from '@vue/composition-api'
import odbStoreModule from './odbStoreModule'
import useODB from './useODB'
import {
    mdiCloseCircle,
    mdiHelpCircle,
    mdiCheckCircle,
    mdiCodeJson,
    mdiViewGrid,
    mdiArrowRight,
    mdiArrowLeft,
    mdiLinkVariant,
    mdiFileDelimitedOutline,
    mdiFolderSearchOutline,
    mdiInformation,
    mdiPlayCircle,
    mdiAlertCircle,
    mdiCheckDecagram,
    mdiAlert,
    mdiFlag
} from '@mdi/js'

export default {
  name: 'DatamapChecker',
  props: {
    // We need to know which country this mapping belongs to!
    countryId: {
      type: [Number, String],
      required: false,
      default: 318
    }
  },
  setup(props) {
    const ODB_APP_STORE_MODULE_NAME = 'app-odb'
    if (!store.hasModule(ODB_APP_STORE_MODULE_NAME)) store.registerModule(ODB_APP_STORE_MODULE_NAME, odbStoreModule)
    onUnmounted(() => {
        if (store.hasModule(ODB_APP_STORE_MODULE_NAME)) store.unregisterModule(ODB_APP_STORE_MODULE_NAME)
    })

    const { fetchDatamap, fetchDatamapMapping, saveDatamapMapping } = useODB()
    
    const isSaving = ref(false)
    const dbMappingStatus = ref(null)

    // Upgraded standard list to an array of objects
    const defaultLabelsArray = [
        {"label":"uuid"},
      {"label":"db_ID_pack"},
      {"label":"vShopTimeSeconds"},
      {"label":"O_ShoppingObservation1","scale":2},
      {"label":"O_ShoppingObservation2","scale":4},
      {"label":"CategoryDriverOE"},
      {"label":"P_VisualCues_COLORS"},
      {"label":"P_VisualCues_SHAPE"},
      {"label":"P_VisualCues_LOGO"},
      {"label":"P_VisualCues_VARIANT"},
      {"label":"P_VisualCues_INGREDIENTS"},
      {"label":"P_VisualCues_VISUALS"},
      {"label":"P_ShoppingMotivationOE"},
      {"label":"P_ShoppingBarriersOE"},
      {"label":"P_ShoppingBarriers_OTHER_BRAND"},
      {"label":"P_ShoppingBarriers_UNFAMILIAR"},
      {"label":"P_ShoppingBarriers_USUAL"},
      {"label":"P_ShoppingBarriers_NOTICE"},
      {"label":"P_ShoppingBarriers_DESINTEREST"},
      {"label":"P_ShoppingBarriers_UNDERSTAND"},
      {"label":"P_ShoppingBarriers_BRAND"},
      {"label":"P_ShoppingBarriers_PRODUCT"},
      {"label":"P_ShoppingBarriers_EXPENSIVE"},
      {"label":"P_ShoppingBarriers_UNAPPEALING"},
      {"label":"P_ShoppingBarriers_MEET_NEEDS"},
      {"label":"vFindCorrect"},
      {"label":"FindTimeSeconds"},
      {"label":"vFindTimeSeconds"},
      {"label":"quantity_test"},
      {"label":"quantity_range"},
      {"label":"quantity_total"},
      {"label":"turnover_test"},
      {"label":"turnover_range"},
      {"label":"turnover_total"},
      {"label":"EaseOfFind","scale":5},
      {"label":"vEaseOfFind","scale":5},
      {"label":"F_Flash1_OE"},
      {"label":"F_Flash2_OE"},
      {"label":"F_Flash_Recall","scale":2},
      {"label":"F_FLASH3_FIT","scale":10},
      {"label":"F_FLASH3_UND","scale":10},
      {"label":"F_FLASH3_USUAL","scale":10},
      {"label":"F_FLASH3_QUAL","scale":10},
      {"label":"F_FLASH3_CLEAR","scale":10},
      {"label":"F_FLASH3_EXPENSIVE","scale":10},
      {"label":"F_FLASH3_TASTE","scale":10},
      {"label":"F_FLASH3_HEALTHY","scale":10},
      {"label":"F_FLASH3_EFFECTIVE","scale":10},
      {"label":"F_FLASH3_SUITABLE","scale":10},
      {"label":"F_FLASH3_WANTBUY","scale":10},
      {"label":"F_Emotion","scale":10},
      {"label":"F_DifferentiationAfterFlash","scale":5},
      {"label":"Q_InitialImpressionsOE"},
      {"label":"Q_OA_5pts","scale":5},
      {"label":"Q_OA_4pts","scale":4},
      {"label":"Q_DESIGNAPPEAL_DESIGN","scale":4},
      {"label":"Q_DESIGNAPPEAL_COLORS","scale":4},
      {"label":"Q_DESIGNAPPEAL_BRAND","scale":4},
      {"label":"Q_DESIGNAPPEAL_NAME","scale":4},
      {"label":"Q_DESIGNAPPEAL_PRODUCTFONT","scale":4},
      {"label":"Q_DESIGNAPPEAL_VARIANTFONT","scale":4},
      {"label":"Q_DESIGNAPPEAL_INGREDIENTS","scale":4},
      {"label":"Q_DESIGNAPPEAL_PICTURE","scale":4},
      {"label":"Q_DESIGNAPPEAL_DRAWING","scale":4},
      {"label":"Q_DESIGNAPPEAL_CLAIM","scale":4},
      {"label":"Q_DESIGNAPPEAL_6pts_DESIGN","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_COLORS","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_BRAND","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_NAME","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_PRODUCTFONT","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_VARIANTFONT","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_INGREDIENTS","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_PICTURE","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_DRAWING","scale":6},
      {"label":"Q_DESIGNAPPEAL_6pts_CLAIM","scale":6},
      {"label":"Q_PACKATT_PREM"},
      {"label":"Q_PACKATT_UNIQUE"},
      {"label":"Q_PACKATT_MODERN"},
      {"label":"Q_PACKATT_APP"},
      {"label":"Q_PACKATT_UND"},
      {"label":"Q_PACKATT_EXCITING"},
      {"label":"Q_PACKATT_INFORM"},
      {"label":"Q_PACKATT_CLUTTER"},
      {"label":"Q_PACKATT_CONFUSE"},
      {"label":"Q_PACKATT_OUT"},
      {"label":"Q_PACKATT_None"},
      {"label":"Q_COMMUNICATION_QUALITY","scale":5},
      {"label":"Q_COMMUNICATION_LIKEME","scale":5},
      {"label":"Q_COMMUNICATION_TRUST","scale":5},
      {"label":"Q_COMMUNICATION_BETTER","scale":5},
      {"label":"Q_COMMUNICATION_HEALTHY","scale":5},
      {"label":"Q_COMMUNICATION_TASTE","scale":5},
      {"label":"Q_COMMUNICATION_EFFECT","scale":5},
      {"label":"Q_COMMUNICATION_ECO","scale":5},
      {"label":"Q_COMMUNICATION2_TEST_VALUE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_QUALITY","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_TASTE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_EXPENSIVE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_MODERN","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_APPEAL","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_TRUSTWORTHY","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_RELEVANCE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_EFFECTIVE","scale":10},
      {"label":"Q_COMMUNICATION2_TEST_SUITABLE","scale":10},
      {"label":"Q_OverallLiking_TEST","scale":10},
      {"label":"vF_IATQUALITY"},
      {"label":"vF_IATTRUST"},
      {"label":"vF_IATMODERN"},
      {"label":"vF_IATUNIQUE"},
      {"label":"vF_IATTASTE"},
      {"label":"vF_IATPACKAGING"},
      {"label":"vF_IATPURCHASE"},
      {"label":"Q_PropensityToBuy_Unpriced","scale":5},
      {"label":"Q_PropensityToBuy","scale":5},
      {"label":"Q_Price_Perception","scale":5},
      {"label":"Q_Value","scale":5},
      {"label":"Q_PI","scale":5},
      {"label":"Q_Fit_Concept","scale":4},
      {"label":"Q_Benefit_PostConcept","scale":3},
      {"label":"Q_PI_postconcept","scale":3},
      {"label":"Q_Fit_brand","scale":4},
      {"label":"Q_different","scale":4},
      {"label":"Q_Benefit","scale":4},
      {"label":"Q_Benefit2","scale":3},
      {"label":"Q_BenefitOE"},
      {"label":"Q_Meets_needs","scale":3},
      {"label":"Q_Missing_prod","scale":3},
      {"label":"w_main"},
      {"label":"w_user"},
      {"label":"db_rep_sample","scale":2},
      {"label":"db_cell_ad","scale":2},
      {"label":"db_written_concept","scale":2},
      {"label":"db_past_purchased","scale":2},
      {"label":"db_bumo","scale":2},
      {"label":"db_sample","scale":2},
      {"label":"DeviceUsed","scale":4},
      {"label":"S_Gender","scale":4},
      {"label":"S_Age"},
      {"label":"S_FrequencyTB","scale":7},
      {"label":"S_Brand_PastPurchasedTB"},
      {"label":"S_Brand_BUMOTB"},
      {"label":"S_HHsize","scale":6},
      {"label":"S_NbChildren","scale":7},
      {"label":"S_AgeChildrenr1"},
      {"label":"S_AgeChildrenr2"},
      {"label":"S_AgeChildrenr3"},
      {"label":"S_AgeChildrenr4"},
      {"label":"S_AgeChildrenr5"},
      {"label":"S_AgeChildrenr6"},
      {"label":"S_AgeChildrenr7"},
      {"label":"S_AgeChildrenr8"},
      {"label":"S_AgeChildrenr9"},
      {"label":"S_AgeChildrenr10"},
      {"label":"S_AgeChildrenr11"},
      {"label":"S_AgeChildrenr12"},
      {"label":"S_AgeChildrenr13"},
      {"label":"S_AgeChildrenr14"},
      {"label":"S_AgeChildrenr15"},
      {"label":"S_AgeChildrenr16"},
      {"label":"S_AgeChildrenr17"},
      {"label":"S_AgeChildrenr18"},
      {"label":"S_CurrentHair_Color","scale":7},
      {"label":"S_Hair_Length","scale":7},
      {"label":"S_Hair_Texture_6","scale":6},
      {"label":"S_Hair_Texture_8","scale":8},
      {"label":"C_Marital","scale":6},
      {"label":"C_Education_US","scale":7},
      {"label":"C_Education_UK","scale":7},
      {"label":"C_Education_GER","scale":7},
      {"label":"C_Employment","scale":8},
      {"label":"C_Profession","scale":8},
      {"label":"C_Agglo","scale":4},
      {"label":"C_Ethnicity","scale":7},
      {"label":"C_Hispanic","scale":6},
      {"label":"C_State","scale":51},
      {"label":"C_Store","scale":5},
      {"label":"db_test_vs_comp_eval_order","scale":3},
      {"label":"R_UnaidedBrandOE"},
      {"label":"vUnaidedBrand"},
      {"label":"vAidedRecallTB"},
      {"label":"vAidedRecallRangeTB"},
      {"label":"vAidedRecallRange_wo_innoTB"},
      {"label":"quantity_range_wo_inno"},
      {"label":"P_Click"},
      {"label":"P_PickUp"},
      {"label":"P_BOP"},
      {"label":"P_SOP"},
      {"label":"P_Smell"},
      {"label":"Q_PACKATT_NET_POSITIVE"},
      {"label":"Q_PACKATT_NET_NEGATIVE"},
      {"label":"C_Income_US","scale":7},
      {"label":"C_Income_UK","scale":14},
      {"label":"C_Income_EUR","scale":14}
    ]

    // State refs
    const decipherPath = ref('')
    // Keep it readable JSON for the Textarea format
    const labelsInput = ref(JSON.stringify(defaultLabelsArray, null, 2)) 
    const inputError = ref('')
    const isChecking = ref(false)
    const results = ref([])
    const statusFilter = ref('exact')

    // View Management (table | recap)
    const currentView = ref('table')
    // Table Headers
    const headers = [
      { text: 'Datamap Field (Source)', value: 'datamap_label', width: '25%' },
      { text: 'Status', value: 'status', width: '15%', align: 'center' },
      { text: 'Expected Label (Target)', value: 'mapped_to', width: '30%' },
      { text: 'Scale Warnings', value: 'warnings', width: '30%', sortable: false },
    ]

    // Compute expected objects gracefully based on the user's JSON Input
    const parsedExpectedLabels = computed(() => {
      inputError.value = ''
      if (!labelsInput.value) return []
      try {
        const parsed = JSON.parse(labelsInput.value)
        if (Array.isArray(parsed)) {
          return parsed.filter(item => item && typeof item.label === 'string')
        } else {
          inputError.value = 'Input must be a JSON array of objects.'
        }
      } catch (e) {
        inputError.value = 'Invalid JSON format.'
      }
      return []
    })

    const isValidForm = computed(() => 
      decipherPath.value.length > 3 && 
      parsedExpectedLabels.value.length > 0 && 
      !inputError.value
    )

    // Filtering & Counting
    const filteredResults = computed(() => {
      if (statusFilter.value === 'all') return results.value
      if (statusFilter.value === 'matched') return results.value.filter(r => r.status === 'matched' || r.status === 'grid_matched')
      return results.value.filter(r => r.status === statusFilter.value)
    })

    // List of labels mapped or suggested for the UI Expansion Panel
    const UnmappedLabels = computed(() => {
      return dropdownOptions.value
        .filter(r => !r.disabled)
        .map(r => r.text)
        .filter(Boolean)
    })

    // const flaggedItems = computed(() => {
    //   const expLabels = parsedExpectedLabels.value.map(e => e.label.toLowerCase())

    //   return results.value.filter(r => {
    //     // Never flag exact matches
    //     if (r.status === 'exact') return false

    //     const dLabel = r.datamap_label.toLowerCase()

    //     // Exclude any QuantityPurchased variables
    //     if (dLabel.includes('quantitypurchased') || dLabel.includes('quantity_purchased')) return false

    //     // Always flag mapped/suggested as they require review
    //     if (r.status === 'mapped' || r.status === 'suggested') return true

    //     // For unmapped, flag them if they share a string root or vgroup with Expected Labels
    //     if (r.status === 'unmapped') {
    //       const vGroup = r.vgroup ? r.vgroup.toLowerCase() : null

    //       for (let exp of expLabels) {
    //         // Match via shared vgroup
    //         if (vGroup && vGroup.length > 2 && exp.includes(vGroup)) {
    //           return true
    //         }

    //         // Match via base prefix of datamap label
    //         const dBase = dLabel.includes('_') ? dLabel.substring(0, dLabel.lastIndexOf('_')) : dLabel
    //         if (dBase.length > 3 && exp.includes(dBase)) {
    //           return true
    //         }

    //         // Match via base prefix of expected label
    //         const expBase = exp.includes('_') ? exp.substring(0, exp.lastIndexOf('_')) : exp
    //         if (expBase.length > 3 && dLabel.includes(expBase)) {
    //           return true
    //         }
    //       }
    //     }
    //     return false
    //   })
    // })

    const flaggedItems = computed(() => {
      // Exact match list from Python
      const exactMatches = [
        "P_CategoryDriverOE", "Q_FitBrand", "Q_OA", 
        "O_ShoppingObservation", "R_UnaidedRecall"
      ]

      // Keyword "includes" list from Python
      const keywords = [
        "P_VisualCues_r", "P_ShoppingObservation", "P_ShoppingBarriers_r",
        "F_FLASH3_r", "Q_DESIGNAPPEAL_r", "Q_PACKATT_r", "Q_PackAtt_",
        "Q_Communication", "Q_COMMUNICATION2_c", "Q_COMMUNICATION2_r",
        "Q_COMMUNICATION2_TEST_c", "QX_P_VisualCues", "QX_P_ShoppingObservation",
        "QX_P_ShoppingBarriers", "QX_F_FLASH3", "QX_Q_DESIGNAPPEAL",
        "QX_Q_PACKATT", "QX_Q_PackAtt", "QX_Q_Communication",
        "QX_Q_COMMUNICATION", "vF_IATr", "QX_Q_OA", "C_Income", "C_Education"
      ]

      return results.value.filter(r => {
        // Never flag exact matches
        if (r.status === 'exact') return false

        // We still use lowercase strictly for the QuantityPurchased check
        const dLabelLower = r.datamap_label.toLowerCase()

        // Exclude any QuantityPurchased variables
        if (dLabelLower.includes('quantitypurchased') || dLabelLower.includes('quantity_purchased')) return false

        // Always flag mapped/suggested as they require review
        if (r.status === 'mapped' || r.status === 'suggested') return true

        // For unmapped, flag them based on the specific labels/keywords
        if (r.status === 'unmapped') {
          // Use original case to match Python's case-sensitive logic
          const originalLabel = r.datamap_label 

          // 1. Check if the label exactly matches the list
          if (exactMatches.includes(originalLabel)) {
            return true
          }

          // 2. Check if the label contains any of the keywords
          if (keywords.some(keyword => originalLabel.includes(keyword))) {
            return true
          }
        }
        
        return false
      })
    })


    const exactCount = computed(() => results.value.filter(r => r.status === 'exact').length)
    const unmappedCount = computed(() => results.value.filter(r => r.status === 'unmapped').length)
    const suggestedCount = computed(() => results.value.filter(r => r.status === 'suggested').length)
    const mappedCount = computed(() => results.value.filter(r => r.status === 'mapped').length)
    
    const itemsWithWarnings = computed(() => {
      return results.value.filter(r => r.warnings && r.warnings.length > 0)
    })

    const hasBlockingErrors = computed(() => {
      // Returns true if there is at least one active warning of type 'error'
      return results.value.some(r => r.warnings && r.warnings.some(w => w.type === 'error'))
    })

    const usedExpectedLabels = computed(() => {
      return results.value.map(r => r.mapped_to).filter(Boolean)
    })

    const dropdownOptions = computed(() => {
      // Deduplicate
      const uniqueExpected = [...new Set(parsedExpectedLabels.value.map(l => l.label))]

      return uniqueExpected.map(label => ({
        text: label,
        value: label,
        disabled: usedExpectedLabels.value.includes(label)
      }))
    })

    const removeWarning = (item, warnIndex) => {
      item.warnings.splice(warnIndex, 1)
    }

    const checkWarnings = (mappedLabel, actualValues) => {
      const warnings = []
      if (!mappedLabel) return warnings

      const expectedObj = parsedExpectedLabels.value.find(e => e.label === mappedLabel)
      const expectedScale = expectedObj ? expectedObj.scale : null

      const hasValues = actualValues && Array.isArray(actualValues) && actualValues.length > 0
      const actualLength = hasValues ? actualValues.length : 0

      // Error: Scale Mismatch
      if (expectedScale && actualLength !== expectedScale) {
        warnings.push({
          id: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'error',
          text: `Check scale: Expected ${expectedScale}, found ${actualLength}`,
          raw_values: actualValues
        })
      }

      // Warning: Reversed Scale
      if (hasValues) {
        const firstValue = actualValues[0].value
        if (firstValue !== 1 && firstValue === actualLength) {
          warnings.push({
            id: `warn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'warning',
            text: `Reversed scale: Starts with ${firstValue}`,
            raw_values: actualValues
          })
        }
      }

      return warnings
    }

    const onMappingChange = (item) => {
      if (item.mapped_to) {
        item.status = item.mapped_to === item.datamap_label ? 'exact' : 'mapped'
      } else {
        item.status = 'unmapped'
      }
      item.warnings = checkWarnings(item.mapped_to, item.raw_values)
    }

    // Loading Animations
    const loadingMessages = [
      'Connecting to Decipher...',
      'Retrieving datamap layout...',
      'Cross-referencing expected scales & labels...',
      'Calculating fuzzy matches for missing variables...',
      'Validating array and choices constraints...'
    ]
    const currentMessageIndex = ref(0)
    let loadingInterval = null

    const currentLoadingText = computed(() => loadingMessages[currentMessageIndex.value])

    const startLoadingAnimation = () => {
      currentMessageIndex.value = 0
      loadingInterval = setInterval(() => {
        if (currentMessageIndex.value < loadingMessages.length - 1) currentMessageIndex.value++
      }, 500)
    }

    const stopLoadingAnimation = () => {
      if (loadingInterval) clearInterval(loadingInterval)
    }

    const getStatusColor = (status) => {
       const map = { 
        'unmapped': 'error', 
        'suggested': 'warning', 
        'mapped': 'primary', 
        'exact': 'success' 
      }
      return map[status] || 'grey'
    }

    const getStatusIcon = (status) => {
      const map = { 
        'unmapped': mdiCloseCircle, 
        'suggested': mdiHelpCircle, 
        'mapped': mdiCheckCircle,
        'exact': mdiCheckDecagram
      }
      return map[status] || mdiInformation
    }

    const levenshtein = (a, b) => {
      const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
      for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
      for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
        }
      }
      return matrix[a.length][b.length];
    }

    const extractAllLabelsWithValues = (datamapArray) => {
      const map = new Map()
      const vgroupMap = new Map()

      // Pass 1: Build a registry mapping vgroup prefixes to all siblings
      datamapArray.forEach(q => {
        if (q.vgroup) {
          if (!vgroupMap.has(q.vgroup)) vgroupMap.set(q.vgroup, [])
          vgroupMap.get(q.vgroup).push({
            label: q.label,
            subId: q.row || q.col || q.choice || null,
            title: q.colTitle || q.rowTitle || q.title || q.qtitle || ''
          })
        }
      })

      // Pass 2: Process actual datamap into items
      datamapArray.forEach(question => {
        const parentValues = question.values || []
        const parentRows = question.rows || []
        const parentCols = question.cols || question.choices || question.answers || question.elements || []
        const parentType = question.type || 'text'
        const siblings = question.vgroup ? vgroupMap.get(question.vgroup) : []
        
        if (question.label) {
          map.set(question.label.trim(), { 
            values: parentValues, 
            type: parentType,
            vgroup: question.vgroup || null,
            title: question.colTitle || question.rowTitle || question.title || question.qtitle,
            parentRows: parentRows,
            parentCols: parentCols,
            siblings: siblings,
            subId: question.row || question.col || question.choice || null
          })
        }
        
        if (question.variables && Array.isArray(question.variables)) {
          question.variables.forEach(v => {
            if (v.label) {
              const vSiblings = v.vgroup ? vgroupMap.get(v.vgroup) : siblings
              map.set(v.label.trim(), { 
                values: v.values || parentValues, 
                type: v.type || parentType,
                vgroup: v.vgroup || question.vgroup || null,
                title: v.colTitle || v.rowTitle || v.title || v.qtitle || question.rowTitle || question.colTitle || question.title || question.qtitle,
                parentRows: parentRows,
                parentCols: parentCols,
                siblings: vSiblings,
                subId: v.row || v.col || v.choice || null
              })
            }
          })
        }
      })
      
      return Array.from(map.entries()).map(([label, data]) => ({ 
        datamap_label: label, 
        raw_values: data.values,
        type: data.type,
        vgroup: data.vgroup,
        title: data.title,
        parentRows: data.parentRows,
        parentCols: data.parentCols,
        siblings: data.siblings,
        subId: data.subId
      }))
    }

    const runCheck = async () => {
      if (!isValidForm.value) return
      
      isChecking.value = true
      currentView.value = 'table' 
      startLoadingAnimation()

      try {

        const savedDBMapping = await fetchDatamapMapping({ project_country: props.countryId })


        if (savedDBMapping) {
           // Auto-fill the UI on the left hand side!
           decipherPath.value = savedDBMapping.decipher_path || ''
           dbMappingStatus.value = savedDBMapping.is_final
        }

        if (!decipherPath.value) {
            inputError.value = "No Decipher path associated with this project. Please configure it in the list view."
            stopLoadingAnimation()
            isChecking.value = false
            return
        }

        // 2. SECOND: Fetch the actual Datamap from Decipher using the path we just grabbed
        const resp = await fetchDatamap({ path: decipherPath.value })
        const rawDatamap = resp.variables
        const availableDmItems = extractAllLabelsWithValues(rawDatamap)
        const initResults = availableDmItems.map(dm => ({
            datamap_label: dm.datamap_label, 
            type: dm.type,
            raw_values: dm.raw_values,
            vgroup: dm.vgroup,
            title: dm.title,
            parentRows: dm.parentRows,
            parentCols: dm.parentCols,
            siblings: dm.siblings,
            subId: dm.subId,
            mapped_to: null, 
            status: 'unmapped',
            warnings: []
        }))

        let remainingExpectedStr = [...new Set(parsedExpectedLabels.value.map(e => e.label))]

        // --- PASS 1: Exact 1:1 Match ---
        initResults.forEach(row => {
            const matchIndex = remainingExpectedStr.findIndex(e => e === row.datamap_label)
            if (matchIndex !== -1) {
                row.mapped_to = remainingExpectedStr[matchIndex]
                row.status = 'exact'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(matchIndex, 1)
            }
        })

        // --- PASS 2: Prefix Matches ---
        initResults.filter(r => r.status === 'unmapped').forEach(row => {
            const matchIndex = remainingExpectedStr.findIndex(e => row.datamap_label.startsWith(e) && e !== row.datamap_label)
            if (matchIndex !== -1) {
                row.mapped_to = remainingExpectedStr[matchIndex]
                row.status = 'mapped'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(matchIndex, 1)
            }
        })

        // --- PASS 3: Fuzzy Matches ---
        initResults.filter(r => r.status === 'unmapped').forEach(row => {
            const dmLower = row.datamap_label.toLowerCase()
            let bestIdx = -1
            let bestScore = Infinity

            remainingExpectedStr.forEach((e, idx) => {
                const eLower = e.toLowerCase()
                if (eLower.includes(dmLower) || dmLower.includes(eLower)) {
                    bestIdx = idx
                    bestScore = 0
                } else {
                    const dist = levenshtein(eLower, dmLower)
                    if (dist <= 3 && dist < bestScore) {
                        bestScore = dist
                        bestIdx = idx
                    }
                }
            })

            if (bestIdx !== -1 && bestScore <= 3) {
                row.mapped_to = remainingExpectedStr[bestIdx]
                row.status = 'suggested'
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                remainingExpectedStr.splice(bestIdx, 1)
            }
        })

        if (savedDBMapping && savedDBMapping.mapping_data && savedDBMapping.mapping_data.length > 0) {
          const dbData = savedDBMapping.mapping_data
          dbData.forEach(dbItem => {
             const row = initResults.find(r => r.datamap_label === dbItem.label)
             if (row) {
                row.mapped_to = dbItem.expected_label
                row.status = dbItem.status
                row.warnings = checkWarnings(row.mapped_to, row.raw_values)
                
                // Remove from remaining available dropdown options
                const idx = remainingExpectedStr.indexOf(dbItem.expected_label)
                if (idx > -1) remainingExpectedStr.splice(idx, 1)
             }
          })
        }

        results.value = initResults
        statusFilter.value = 'exact'

      } catch (error) {
        console.error('Failed to validate datamap:', error)
      } finally {
        stopLoadingAnimation()
        isChecking.value = false
      }
    }

    const exportJSON = () => {
      const exportedVariables = results.value
        .filter(item => ['exact', 'mapped', 'suggested'].includes(item.status))
        .map(item => {
          const exportObj = {
            type: item.type,
            label: item.datamap_label,
            expected_label: item.mapped_to,
            status: item.status
          }

          if (item.raw_values && item.raw_values.length > 0) {
            const length = item.raw_values.length
            const firstValue = item.raw_values[0].value
            if (firstValue !== 1 && firstValue === length) {
              exportObj.reverse_scale = length
            }
          }

          return exportObj
        })

      const finalJSON = { variables: exportedVariables }
      
      const blob = new Blob([JSON.stringify(finalJSON, null, 2)], { type: 'application/json;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'mapped_variables.json')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const exportCSV = () => {
      if (results.value.length === 0) return

      let csvContent = '"Datamap Label","Mapped Expected Label","Warnings"\n'

      results.value.forEach(item => {
        const expected = item.mapped_to || 'UNMAPPED'
        const warnings = item.warnings && item.warnings.length > 0 
          ? item.warnings.map(w => w.text).join(' | ') 
          : ''
        const safeDm = item.datamap_label.replace(/"/g, '""')
        const safeExpected = expected.replace(/"/g, '""')
        const safeWarn = warnings.replace(/"/g, '""')

        csvContent += `"${safeDm}","${safeExpected}","${safeWarn}"\n`
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'datamap_to_expected_mapping.csv')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const saveToDatabase = async (isFinal = false) => {
      isSaving.value = true
      try {
        // Generate the exact same JSON array you use for exportJSON()
        const exportedVariables = results.value
          .filter(item => ['exact', 'mapped', 'suggested'].includes(item.status))
          .map(item => {
            const exportObj = {
              type: item.type,
              label: item.datamap_label,
              expected_label: item.mapped_to,
              status: item.status
            }
            if (item.raw_values && item.raw_values.length > 0) {
              const length = item.raw_values.length
              const firstValue = item.raw_values[0].value
              if (firstValue !== 1 && firstValue === length) {
                exportObj.reverse_scale = length
              }
            }
            return exportObj
          })

        await saveDatamapMapping({
          project_country: props.countryId,
          mapping_data: exportedVariables,
          is_final: isFinal
        })
        
        dbMappingStatus.value = isFinal
        // If you have a toast library installed:
        // const toast = useToast(); toast.success(isFinal ? "Mapping finalized!" : "Draft saved!");
        alert(isFinal ? "Mapping finalized successfully!" : "Draft saved successfully!");
        
      } catch (e) {
        alert("Failed to save mapping to database.");
      } finally {
        isSaving.value = false
      }
    }

    onMounted(() => {
      runCheck()
    })

    return {
      decipherPath,
      labelsInput,
      inputError,
      isChecking,
      results,
      statusFilter,
      headers,
      isValidForm,
      filteredResults,
      UnmappedLabels,
      flaggedItems,
      currentLoadingText,
      currentView,
      runCheck,
      getStatusColor,
      getStatusIcon,
      exportCSV,
      exportJSON,

      exactCount,
      unmappedCount,
      suggestedCount,
      mappedCount,
      usedExpectedLabels,
      onMappingChange,
      dropdownOptions,
      removeWarning,
      hasBlockingErrors,
      itemsWithWarnings,
      saveToDatabase,
      isSaving,
      dbMappingStatus,

      icons: {
        mdiArrowRight,
        mdiArrowLeft,
        mdiLinkVariant,
        mdiFileDelimitedOutline,
        mdiFolderSearchOutline,
        mdiPlayCircle,
        mdiAlertCircle,
        mdiAlert,
        mdiCodeJson,
        mdiFlag,
        
        mdiCheckCircle,
        mdiCheckDecagram,
      }
    }
  }
}
</script>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace;
}
.position-relative {
  position: relative;
}
.max-w-sm {
  max-width: 400px;
}
/* Smooth fade transition for rotating text */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>